// The pieces of the review scan's bar that must exist before the scan's code
// has loaded: the frame, its buttons, and the "Analyzing" state, so the bar
// is up the moment the button is pressed and does not change size when the
// real one takes over. Main bundle; small on purpose.

import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Play, Repeat, ScanEye, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { CTRL } from './ctrl';

/** The bar: dark glass, one row. A container, so its parts can shrink to
 *  the width it is given, never to what they happen to say. */
export const BAR_ROOT = cn(CTRL, '@container relative h-9 w-full justify-start gap-0.5 px-1 hover:bg-black/60');

// Buttons tighten on a narrow bar (under 360 px) so the track keeps a usable width.
export const BAR_BTN =
  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/85 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/60 @max-[360px]:w-6';

/** Shown as unavailable: dimmed, no hover, still under the pointer so its
 *  tooltip can say why (a `disabled` button gets no pointer events). */
export const BAR_UNAVAILABLE = 'cursor-not-allowed opacity-40 hover:bg-transparent';

/** Fixed widths: the track takes what is left, and that never changes with
 *  what the other parts say. */
export const PCT_W = 'w-9 @max-[360px]:w-8';
/** The scan area ("Image" / "Area") and the speed: each names its choice,
 *  an icon alone on a narrow bar. */
export const AREA_W = 'w-[66px] @max-[360px]:w-7';
/** Tissue only, switch and sensitivity chevron: the switch alone (no word)
 *  on a narrow bar, and no chevron on the narrowest. */
export const TISSUE_W = 'w-[78px] @max-[360px]:w-11 @max-[300px]:w-8';
export const SPEED_W = 'w-[74px] @max-[360px]:w-7';
/** Restart gives way on the narrowest bars: closing and reopening replans the same. */
export const RESTART_HIDE = '@max-[360px]:hidden';
/** So does "Show visited areas", narrower still (the minimap shows the
 *  same): below this the rest would not fit. */
export const ON_IMAGE_HIDE = '@max-[272px]:hidden';

export function BarButton({
  label,
  onClick,
  unavailable,
  pressed,
  className,
  children,
}: {
  label: string;
  onClick?: () => void;
  unavailable?: boolean;
  /** A toggle: whether it is on (lit, and said so). */
  pressed?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-disabled={unavailable || undefined}
          aria-pressed={pressed}
          onClick={unavailable ? undefined : onClick}
          // A mouse click must not leave focus here: the next Space would
          // press this button again as well as playing or pausing.
          onMouseUp={(e) => e.currentTarget.blur()}
          className={cn(BAR_BTN, pressed && 'bg-white/15 text-white', unavailable && BAR_UNAVAILABLE, className)}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

/** While the tissue is being found the whole bar says so: the controls stay
 *  where they are but invisible (so the bar keeps its size), and one label
 *  sits across the middle (the user, 2026-10-01: "cover the whole bar"). */
export function AnalyzingCover() {
  return (
    <div
      role="status"
      aria-label="Analyzing"
      className="absolute inset-0 flex items-center justify-center gap-2 rounded-md text-xs text-white/90"
    >
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      Analyzing
    </div>
  );
}

/** The controls of a bar, hidden (keeping their space) while it analyzes. */
export function BarControls({ hidden, children }: { hidden: boolean; children: ReactNode }) {
  return (
    <div className={cn('contents', hidden && 'invisible')} aria-hidden={hidden || undefined}>
      {children}
    </div>
  );
}

/** The track's footprint, for the bar that has no scan yet. */
export function TrackPlaceholder() {
  return <div className="relative mx-1 h-8 min-w-[32px] flex-1" />;
}

/** The bar while the scan's code is still on its way: the real bar's shape,
 *  covered by "Analyzing" like the real one while it looks for tissue. */
export function PendingScanBar() {
  return (
    <div role="group" aria-label="Guided review" className={BAR_ROOT}>
      <BarControls hidden>
        <BarButton label="Restart from the beginning" unavailable className={RESTART_HIDE}>
          <Repeat className="h-4 w-4" />
        </BarButton>
        <BarButton label="Previous area" unavailable>
          <ChevronLeft className="h-4 w-4" />
        </BarButton>
        <BarButton label="Play" unavailable>
          <Play className="h-4 w-4" />
        </BarButton>
        <BarButton label="Next area" unavailable>
          <ChevronRight className="h-4 w-4" />
        </BarButton>
        <TrackPlaceholder />
        <span className={cn(PCT_W, 'shrink-0')} />
        <span className={cn(AREA_W, 'h-7 shrink-0')} />
        <span className={cn(TISSUE_W, 'h-7 shrink-0')} />
        <span className={cn(SPEED_W, 'h-7 shrink-0')} />
        <BarButton label="Show visited areas" unavailable className={ON_IMAGE_HIDE}>
          <ScanEye className="h-4 w-4" />
        </BarButton>
        <BarButton label="Close guided review" unavailable>
          <X className="h-4 w-4" />
        </BarButton>
      </BarControls>
      <AnalyzingCover />
    </div>
  );
}
